import { type ReactNode, useState } from 'react'
import { VpErrorIcon, VpSparklesIcon } from '@vtmn-play/icons/react'
import AccountInfo from './account-info'
import AccountInput from './account-input'
import { InputType } from './input-type'
import { useTranslation } from 'react-i18next'

interface FieldProps {
  label: string
  type: InputType
  isValid?: boolean
}

interface AccountCardProps {
  title: ReactNode
  referenceValue: number | null
  referenceProgression: number | null
  referenceAiValue?: number | null
  referenceAiProgression?: number | null
  initialForecastValue?: number | null
  initialForecastProgression?: number | null
  valueField?: FieldProps
  formatValue?: (value: number) => string
  onForecastChange?: (value: number | null) => void
}

const computeProgressionFromValue = (value: number, referenceValue: number | null) =>
  referenceValue ? value / referenceValue - 1 : null

const computeValueFromProgression = (progression: number, referenceValue: number | null) =>
  referenceValue != null ? referenceValue + referenceValue * progression : null

const AccountCard = ({
  title,
  referenceValue,
  referenceProgression,
  referenceAiValue,
  referenceAiProgression,
  initialForecastValue,
  initialForecastProgression,
  valueField,
  formatValue,
  onForecastChange
}: AccountCardProps) => {
  const { t } = useTranslation()

  const [forecastValue, setForecastValue] = useState<number | null>(initialForecastValue)
  const [forecastProgression, setForecastProgression] = useState<number | null>(initialForecastProgression)

  const onForecastValueChange = (newForecastValue: number) => {
    setForecastValue(newForecastValue)
    setForecastProgression(computeProgressionFromValue(newForecastValue, referenceValue))
    onForecastChange?.(newForecastValue)
  }

  const onForecastProgressionChange = (newForecastProgression: number) => {
    setForecastProgression(newForecastProgression)
    const newForecastValue = computeValueFromProgression(newForecastProgression, referenceValue)
    setForecastValue(newForecastValue)
    onForecastChange?.(newForecastValue)
  }

  return (
    <div className="mg-channels-account-card">
      <div className="mg-channels-account-card-content">
        <div className="mg-channels-account-card-header">
          <AccountInfo
            title={title}
            value={referenceValue}
            progression={referenceProgression}
            formatValue={formatValue}
          />

          {!!referenceAiValue && (
            <AccountInfo
              title={
                <>
                  {t('general.trajectory.store.edit.channels.account.ai')}{' '}
                  <VpSparklesIcon size={20} color="var(--vp-semantic-color-content-brand)" aria-hidden />
                </>
              }
              value={referenceAiValue}
              progression={referenceAiProgression}
              formatValue={formatValue}
              focusOnValue={true}
            />
          )}
        </div>

        {valueField && (
          <div className="mg-channels-account-card-fields">
            <AccountInput
              title={valueField.label}
              value={forecastValue}
              type={valueField.type}
              onChange={onForecastValueChange}
              isValid={valueField.isValid}
            />
            <AccountInput
              title={t('general.trajectory.store.edit.channels.account.progression')}
              value={forecastProgression}
              type={InputType.PERCENTAGE}
              onChange={onForecastProgressionChange}
            />
          </div>
        )}
        {valueField?.isValid === false && (
          <div className="mg-channels-account-card-error vp-caption" role="alert" aria-live="assertive">
            <VpErrorIcon size={16} />
            {t('general.trajectory.store.edit.channels.account.error', { fieldLabel: valueField.label })}
          </div>
        )}
      </div>
    </div>
  )
}

export default AccountCard
